<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAppearanceStore } from '@/stores/appearance'
import { useAuthStore } from '@/stores/auth'
import { api } from '@/services/api'
import { imageFilter, rgba } from '@/utils/appearance'

const appearance = useAppearanceStore()
const auth = useAuthStore()
const settings = computed(() => appearance.effective)
const source = ref('')
const paused = ref(document.hidden)
let objectUrl = ''
let controller: AbortController | undefined
let generation = 0
watch(
  [
    () => settings.value.backgroundUrl,
    () => settings.value.backgroundMode,
    () => auth.token,
    () => auth.authenticated,
  ],
  async ([url, mode, token, authenticated]) => {
    const current = ++generation
    controller?.abort()
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl)
      objectUrl = ''
    }
    source.value = ''
    if (mode !== 'image' || !url) return
    if (!url.startsWith('/api/v1/settings/appearance/background/')) {
      source.value = url
      return
    }
    if (!authenticated) return
    controller = new AbortController()
    try {
      const blob = await api.backgroundImage(token, url, controller.signal)
      if (current !== generation) return
      objectUrl = URL.createObjectURL(blob)
      source.value = objectUrl
    } catch {
      /* Keep the solid fallback when an image is unavailable. */
    }
  },
  { immediate: true },
)
const style = computed(() => ({
  '--background-gradient': `linear-gradient(${settings.value.gradientAngle}deg,${settings.value.gradientStart},${settings.value.gradientEnd})`,
  '--background-image': source.value ? `url(${JSON.stringify(source.value)})` : 'none',
  '--background-filter': imageFilter(settings.value),
  '--background-overlay': rgba(
    settings.value.imageOverlayColor,
    settings.value.imageOverlayOpacity,
  ),
  '--animation-seconds': `${settings.value.animationSeconds}s`,
}))
function visibilityChanged(): void {
  paused.value = document.hidden
}
onMounted(() => document.addEventListener('visibilitychange', visibilityChanged))
onBeforeUnmount(() => {
  generation++
  controller?.abort()
  if (objectUrl) URL.revokeObjectURL(objectUrl)
  document.removeEventListener('visibilitychange', visibilityChanged)
})
</script>
<template>
  <div
    class="page-background"
    aria-hidden="true"
    :style="style"
    :class="{ 'background-paused': paused }"
  >
    <div
      v-if="settings.backgroundMode === 'gradient'"
      class="background-gradient"
      :class="{ 'gradient-flow': settings.gradientAnimated }"
    />
    <template v-else-if="settings.backgroundMode === 'image' && source">
      <div class="background-image" :class="{ 'image-drift': settings.imageMotion === 'drift' }" />
      <div
        class="background-overlay"
        :class="{ 'image-breathe': settings.imageMotion === 'breathe' }"
      />
    </template>
  </div>
</template>
